import React from "react"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { AboutThisExpenseWhatKindOfJourney } from "./WhatKindOfJourney"
import { defineMessage, MessageDescriptor, useIntl } from "react-intl"
import { useFlags } from "launchdarkly-react-client-sdk"
import useBenefit from "../../dynamic-content/hooks/useGetBenefit"
import { JourneySubTypes } from "./ExpenseJourneyTypes"

type WhatKindOfFertilityCareOption = {
  label: MessageDescriptor
  value: string
  pfpt?: boolean
}

const fertilityCareOptions: WhatKindOfFertilityCareOption[] = [
  {
    value: "IUI",
    label: defineMessage({ defaultMessage: "Intrauterine insemination (IUI)" })
  },
  {
    value: "IVF",
    label: defineMessage({ defaultMessage: "In vitro fertilization (IVF)" })
  },
  {
    value: JourneySubTypes.PELVIC_FLOOR_THERAPY_VIRTUAL,
    label: defineMessage({ defaultMessage: "Pelvic floor physical therapy: Virtual" }),
    pfpt: true
  },
  {
    value: JourneySubTypes.PELVIC_FLOOR_THERAPY_IN_PERSON,
    label: defineMessage({ defaultMessage: "Pelvic floor physical therapy: In person" }),
    pfpt: true
  },
  // "Other" stays last in the list
  {
    value: "PATHFINDING",
    label: defineMessage({ defaultMessage: "Other" })
  }
]

export const AboutThisExpenseWhatKindOfFertilityCare = (): JSX.Element => {
  const { company, hasMetSignatureRequirement } = useCurrentUser()
  const hasIvfRequirement = company?.hasIvfRequirements
  const intl = useIntl()
  const { pelvicFloorTherapyReimbursementLaunched } = useFlags()
  const { data: benefit } = useBenefit()
  const isPfptEnabled = Boolean(
    pelvicFloorTherapyReimbursementLaunched && benefit?.pelvicFloorTherapyTelemedicine?.hasEligibleExpenses
  )

  const fertilityCareHeaderText = intl.formatMessage({
    defaultMessage: "What kind of fertility care is this related to?"
  })
  const fertilityCareBodyText = intl.formatMessage({
    defaultMessage: "If you're not sure, choose the option that seems like the best fit."
  })
  // if the user's company has an ivf requirement and the user has not met the signature requirement, we need to show them the IVF info
  const needsIvfInfo = hasIvfRequirement && !hasMetSignatureRequirement
  const formattedTypeOfFertilityCareOptions = fertilityCareOptions
    .filter((option) => !option.pfpt || isPfptEnabled)
    .map((typeOfFertilityCareOption) => ({
      value: typeOfFertilityCareOption.value,
      label: intl.formatMessage(typeOfFertilityCareOption.label)
    }))

  return (
    <AboutThisExpenseWhatKindOfJourney
      headerText={fertilityCareHeaderText}
      bodyText={fertilityCareBodyText}
      selectOptions={formattedTypeOfFertilityCareOptions}
      hasRequirements={needsIvfInfo}
    />
  )
}
